// src/types/quotes.ts

import { Client } from "@/types/clients";
import { VirtualMarketFullSchema } from "@/types/vmarkets";
import { PricedRouteSchema } from "@/types/prices";
import { Asset } from "@/types/assets";

export enum QuoteStatus {
  ACTIVE = "active",
  ACCEPTED = "accepted",
  EXECUTED = "executed",
  EXPIRED = "expired",
  CANCELLED = "cancelled",
}

export enum AcceptedQuoteStatus {
  ACTIVE = "active",
  EXECUTED = "executed",
  CANCELLED = "cancelled",
}

export enum QuoteUnit {
  BASE = "base",
  COUNTER = "counter",
}

export enum QuoteSide {
  BUY = "buy",
  SELL = "sell",
}

export interface Quote {
  id: number;
  slug: string;
  client: Client;
  vmarket: VirtualMarketFullSchema;
  amount: string;
  unit: QuoteUnit;
  side: QuoteSide;
  fees_bps: string;
  status: QuoteStatus;
  expiration: string;
  created_at: string;
  created_by_email: string;
  initial_price: PricedRouteSchema;
  latest_price: PricedRouteSchema | null;
}

export interface PublicQuote {
  id: number;
  slug: string;
  base: Asset;
  counter: Asset;
  amount: string;
  unit: string;
  side: QuoteSide;
  latest_price: string | null;
  client_name: string;
  expiration: string;
  acceptance: {
    accepted_at: string;
    crypto_address: string | null;
    final_price: string;
    total_from: string;
    total_to: string;
  } | null;
  status: QuoteStatus;
}

export interface DateEnhancedQuote extends Quote {
  expiration_date: Date;
  amount_numeric: number;
}

export interface QuoteCreateRequest {
  vmarket_id: number;
  amount: string;
  unit: QuoteUnit;
  fees_bps: string;
  client_id: string;
  expiration: string;
  side: QuoteSide;
}

export interface QuoteWSUpdate {
  base: string;
  counter: string;
  unit: string;
  amount: string;
  status: QuoteStatus;
  price: string;
  total_from: string;
  total_to: string;
  price_fetched_at: string;
  price_expires_at: string;
  price_ttl_ms: string;
  quote_expiration: string;
}

export interface StatusUpdate {
  status: QuoteStatus;
}

export interface WSUpdate {
  type: "price_update" | "quote_registered" | "quote_unregistered" | "price_update_error" | "status_change";
  slug: string;
  message: string;
  data?: QuoteWSUpdate | StatusUpdate;
}

export interface QuoteAcceptRequest {
  price: string;
  total_from: string;
  total_to: string;
}

export interface QuoteAcceptedResponse {
  status: AcceptedQuoteStatus;
  message: string;
  details: {
    price: string;
    total_from: string;
    total_to: string;
  };
}

export interface AcceptedQuote {
  id: number;
  slug: string;
  client: Client;
  vmarket: VirtualMarketFullSchema;
  amount: string;
  unit: QuoteUnit;
  side: QuoteSide;
  fees_bps: string;
  price: string;
  price_with_fee: string;
  total_from: string;
  total_to: string;
  status: AcceptedQuoteStatus;
  crypto_address: string | null;
  accepted_at: string;
}

export interface DateEnhancedAcceptedQuote extends AcceptedQuote {
  accepted_at_date: Date;
}

export interface AcceptedQuoteAddAddressRequest {
  crypto_address: string;
}
